import { getMessage } from '@tcc/i18n';
import { SEASON_AGNOSTIC } from '../constants/seasonIds';
import {
  FIELD_SUMMARY,
  HARVEST,
  IN_SEASON,
  PLANTING,
} from '../constants/mapLayerIds';

/**
 * Constructs a map layer object from a map layer id.
 * @private
 * @param {String} mapLayerId
 * @param {Object} [layer={}]
 * @returns {Object} - With the mapLayer ID and name
 */
function constructMapLayerObject(mapLayerId, layer = {}) {
  return {
    id: mapLayerId,
    name: getMessage(`layer.${layer.nameId || mapLayerId}`),
  };
}

/**
 * Returns the default map layer for a given season.
 * See CFVWEB-28 and associated Google Doc for details
 * @private
 * @param {Object} layers
 * @param {String} seasonId
 * @return {Object} - With the mapLayer ID and name
 */
function getDefaultMapLayer(layers, seasonId) {
  const layersForSeason = layers[seasonId];

  // show yield map if it exists
  if (layersForSeason[HARVEST.YIELD]) {
    return constructMapLayerObject(
      HARVEST.YIELD,
      layersForSeason[HARVEST.YIELD]
    );
  }

  // otherwise show FHA imagery
  if (layersForSeason[IN_SEASON.FHA]) {
    return constructMapLayerObject(
      IN_SEASON.FHA,
      layersForSeason[IN_SEASON.FHA]
    );
  }

  // otherwise show hybrid map
  if (layersForSeason[PLANTING.HYBRID]) {
    return constructMapLayerObject(
      PLANTING.HYBRID,
      layersForSeason[PLANTING.HYBRID]
    );
  }

  // otherwise show planting pop.
  if (layersForSeason[PLANTING.POPULATION]) {
    return constructMapLayerObject(
      PLANTING.POPULATION,
      layersForSeason[PLANTING.POPULATION]
    );
  }

  // if none of the above exist, show field summary
  return constructMapLayerObject(FIELD_SUMMARY);
}

/**
 * Returns the map layer passed in if it is valid for the season. Otherwise, returns the default.
 * @param {Object} layers
 * @param {String} mapLayerId
 * @param {String} seasonId
 * @return {Object} - With the mapLayer ID and name
 */
export function getMapLayerForSeason(layers, mapLayerId, seasonId) {
  if (mapLayerId === FIELD_SUMMARY) {
    return constructMapLayerObject(FIELD_SUMMARY);
  }

  const layer =
    layers[seasonId][mapLayerId] || layers[SEASON_AGNOSTIC][mapLayerId];

  return layer
    ? constructMapLayerObject(mapLayerId, layer)
    : getDefaultMapLayer(layers, seasonId);
}

/**
 * Determine if the seasonId and mapLayerId present in the URL hash are valid given the seasons.
 * If the seasonId is not present in seasons, return the latest season in seasons
 * by default. Then, for the chosen season, determine if the mapLayerId is valid; if not,
 * return the default map layer.
 * @param {Object} seasons - Object with harvest & planting data for each valid season
 * @param {Array} sortedSeasonKeys - Array of seasons' keys in sorted order
 * @param {Object} layers - Object of all available map layers
 * @param {Object} selectorIds - Map Layer and Season IDs requested in the URL's hash
 * @param {String[]} selectorIds.mapLayerIds
 * @param {String[]} selectorIds.seasonIds
 * @return {Object} - Returns the activeMapLayer and activeSeason, whether they were the ones
 * requested in the URL's hash or default values
 */
export function getActiveSelectorVals(
  seasons,
  sortedSeasonKeys,
  layers,
  selectorIds
) {
  const { mapLayerIds, seasonIds } = selectorIds;

  // If an inequal number of seasons and map layers are requsted, use the min of the 2
  const numberOfViews = Math.min(mapLayerIds.length, seasonIds.length);
  const mapLayerIdsTrimmed = selectorIds.mapLayerIds.slice(0, numberOfViews);
  const seasonIdsTrimmed = selectorIds.seasonIds.slice(0, numberOfViews);

  const activeSeasons = seasonIdsTrimmed.map((seasonId) => {
    const activeSeasonKey =
      (seasonId && sortedSeasonKeys.find((key) => key.includes(seasonId))) ||
      sortedSeasonKeys[0];
    return {
      id: activeSeasonKey,
      name: seasons[activeSeasonKey].name,
    };
  });

  const activeMapLayers = mapLayerIdsTrimmed.map((mapLayerId, index) => getMapLayerForSeason(
      layers,
      mapLayerId || undefined,
      activeSeasons[index].id
    ));
  return {
    activeSeasons,
    activeMapLayers,
  };
}

/**
 * Determine what season and map layer IDs to request from the extracted season
 * @param {Object} selectorIds
 * @param {String} selectorIds.fieldId
 * @param {String[]} selectorIds.mapLayerIds
 * @param {String[]} selectorIds.seasonIds
 * @return {Object} - Return object with requested map layer and season IDs
 */
export function determineRequestedSelectorIds(selectorIds) {
  const { fieldId, mapLayerIds, seasonIds } = selectorIds;

  if (!seasonIds.length || !seasonIds[0]) {
    const currStored =
      JSON.parse(sessionStorage.getItem('dmb-selectors')) || {};
    if (currStored[fieldId]) {
      return {
        mapLayerIds: [currStored[fieldId].mapLayerId],
        seasonIds: [currStored[fieldId].seasonId],
      };
    }
  }
  return { mapLayerIds, seasonIds };
}

/**
 * Update current field's active map layer and session IDs in the browser's sessionStorage
 * @param {String} fieldId
 * @param {String} mapLayerId
 * @param {String} seasonId
 * @return {void}
 */
export function updateSelectorIdsInStorage(fieldId, mapLayerId, seasonId) {
  const currStored = JSON.parse(sessionStorage.getItem('dmb-selectors')) || {};
  const newStored = {
    ...currStored,
    [fieldId]: {
      mapLayerId,
      seasonId,
    },
  };
  sessionStorage.setItem('dmb-selectors', JSON.stringify(newStored));
}
